import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  Button,
  Table,
  Group,
  Text,
  Chip,
  Space,
  Title,
  Grid,
  Card,
  Divider,
  Tabs,
  Badge,
  LoadingOverlay,
  Menu,
  Switch,
  Loader,
  Center,
  TextInput,
  NumberInput,
} from "@mantine/core";
import {
  IconTrash,
  IconCheck,
  IconCircleCheck,
  IconChecks,
} from "@tabler/icons";
import DataDisplayRow from "./dataDisplayRow";
import { useFormatter } from "../hooks/useFomatter";
import { useTranslation } from "react-i18next";
import moment from "moment";
import EntityTabAnchor from "../pages/account/payroll2/EntityTabAnchor";

const formatDebitNotePeriod = (period) => {
  if (!period) return "—";
  const start = period.start ? moment(period.start).format("YYYY-MM-DD") : "";
  const end = period.end ? moment(period.end).format("YYYY-MM-DD") : "";
  if (start && end) return `${start} ~ ${end}`;
  return start || end || "—";
};

const formatDebitNoteDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD") : "—";

const getGiDebitNoteFromRow = (row) => {
  const order =
    row.order && typeof row.order === "object" && row.order._id
      ? row.order
      : null;
  const snap = row.orderSnapShot;
  const isGI = !!(
    order?.isGI ||
    order?.debitNote ||
    snap?.isGI ||
    snap?.debitNote
  );
  if (!isGI) return null;

  const dn = order?.debitNote || snap?.debitNote;
  if (!dn?.code) return null;

  const productTypeName =
    dn.productType?.name ||
    dn.productType?.cname ||
    dn.productTypeName ||
    order?.productType?.name ||
    order?.productType?.cname ||
    null;

  return {
    _id: dn._id,
    code: dn.code,
    type: dn.type || null,
    productTypeName,
    date: dn.date || dn.period?.start || null,
    period: dn.period,
  };
};

const CommissionLinkedPaymentActions = ({ row, rowActions, mt = 20 }) => {
  const {
    handleAcceptPayment,
    handleLinkPayment,
    handleUndoRow,
    handleAcceptAndCreateWaitingPayment,
  } = rowActions;

  const showAcceptPaymentBtn =
    row.status === "PENDING" &&
    row.error &&
    // row.linkedPayments.length <= 1 &&
    row.error.code === "ERR_AMOUNT_NOT_MATCH";

  return (
    <>
      {/* {JSON.stringify(row.error)} */}
      {showAcceptPaymentBtn && (
        <Suspense fallback={<div>Loading...</div>}>
          <Card mt={mt}>
            <Group position="apart">
              <div>
                <Button
                  onClick={() => handleUndoRow(row)}
                  size="xs"
                  color={"red"}
                >
                  Reload
                </Button>
              </div>
              <Group>
                <Menu
                  shadow="md"
                  width={450}
                  withinPortal={true}
                  position="bottom-end"
                >
                  <Menu.Target>
                    <Button size="xs">Handle</Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      onClick={() => handleAcceptPayment(row, { force: true })}
                    >
                      Force accept payment with amount difference
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => handleAcceptAndCreateWaitingPayment(row)}
                    >
                      Accept payment & create waiting payment for remaining
                    </Menu.Item>

                    <Menu.Item onClick={() => handleLinkPayment(row)}>
                      Link more waiting payment
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
          </Card>
        </Suspense>
      )}
    </>
  );
};

const CommissionLinkedPayment = ({
  payment,
  index,
  mt = 20,
  row,
  rowActions,
}) => {
  const { updateRow } = rowActions;
  const [formatter] = useFormatter();
  const { t } = useTranslation();
  const giDebitNote = getGiDebitNoteFromRow(row);

  return (
    <>
      <Suspense fallback={<Loader variant="bars" />}>
        <Card>
          {/* {JSON.stringify(payment)} */}
          <Group position="apart">
            <div>
              <Title order={5}>
                #{index + 1} - {payment.code}
              </Title>
              <Text size="sm" color="dimmed" weight={400}>
                Linked payment
              </Text>
            </div>
            <div>
              <Title order={4}>
                {formatter.currency(payment.acceptedAmount)}
              </Title>
              <Text size="sm" color="dimmed" weight={400}>
                Accepted
              </Text>
            </div>
          </Group>
          <Divider mt={"xs"} mb={"xs"} />

          <div>
            {giDebitNote ? (
              <>
                <Grid>
                  <Grid.Col sm={6}>
                    <Text size="xs">
                      {t(
                        "commissionStatement.linkedPayment.debitNote",
                        "Debit Note"
                      )}
                    </Text>
                  </Grid.Col>
                  <Grid.Col sm={6}>
                    <EntityTabAnchor
                      entity="debitNote"
                      id={giDebitNote._id}
                      label={giDebitNote.code}
                      size="xs"
                    />
                  </Grid.Col>
                </Grid>
                <DataDisplayRow
                  label={t("commissionStatement.ambiguousOrder.dnType", "Type")}
                  value={giDebitNote.type || "—"}
                />
                <DataDisplayRow
                  label={t(
                    "commissionStatement.ambiguousOrder.class",
                    "Class"
                  )}
                  value={giDebitNote.productTypeName || "—"}
                />
                <DataDisplayRow
                  label={t(
                    "commissionStatement.linkedPayment.dnDate",
                    "DN Date"
                  )}
                  value={formatDebitNoteDate(giDebitNote.date)}
                />
                <DataDisplayRow
                  label={t(
                    "commissionStatement.linkedPayment.period",
                    "Period"
                  )}
                  value={formatDebitNotePeriod(giDebitNote.period)}
                />
                <Divider my="xs" />
              </>
            ) : null}
            <DataDisplayRow label="Linked Payment" value={payment.code} />
            <DataDisplayRow
              label="BasePremium"
              value={formatter.currency(payment.premium, payment.baseCurrency)}
            />
            {/* <DataDisplayRow label="Currency" value={payment.currency} /> */}
            <DataDisplayRow
              label="Commission Rate"
              value={`${(payment.commissionRate * 100).toFixed(2)}%`}
            />
            <DataDisplayRow
              label="Expected Period"
              value={payment.expectedPeriod}
            />
            <DataDisplayRow
              label="Expected"
              value={formatter.currency(
                payment.expectedAmount,
                payment.baseCurrency
              )}
            />
            <DataDisplayRow
              label="Unsettled"
              value={formatter.currency(
                payment.unsettledAmount,
                payment.currency
              )}
            />

            <DataDisplayRow
              label="Accepted"
              value={formatter.currency(
                payment.acceptedAmount,
                payment.currency
              )}
            />

            <Switch
              size="xs"
              mt={"xl"}
              label="Full Pay"
              checked={payment.isFull}
              onChange={() => {
                payment.isFull = !payment.isFull;
                updateRow(row);
              }}
            ></Switch>
          </div>
        </Card>
      </Suspense>
    </>
  );
};

const CommissionRowColumnRight = ({ row, rowActions }) => {
  const { handleAcceptPayment } = rowActions;
  return (
    <Suspense fallback={<Loader variant="bars" />}>
      {/* {row.order && <CommissionRowOrderCard id={row.order} row={row} />} */}

      {row.linkedPayments &&
        row.linkedPayments.map((payment, index) => (
          <CommissionLinkedPayment
            key={index}
            payment={payment}
            index={index}
            mt={20}
            row={row}
            rowActions={rowActions}
          />
        ))}
      <CommissionLinkedPaymentActions
        row={row}
        rowActions={rowActions}
        mt={20}
      />
    </Suspense>
  );
};

export default CommissionRowColumnRight;
